import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Star as StarIcon,
  RestaurantMenu as DietIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import dietaryGuideBackground from '../images/dietary-guide.png';

const PAGE_URL = 'https://createadish.com/dietary-guide';
const PAGE_TITLE = 'The Ultimate Dietary Guide: Tailoring AI Recipes to Your Lifestyle - Create A Dish';
const PAGE_DESCRIPTION = 'An in-depth exploration of popular dietary patterns like Keto, Vegan, and Paleo. This guide teaches you how to optimize the Create A Dish generator to meet your specific nutritional goals while discovering the science and benefits behind each eating style.';
const PAGE_IMAGE = 'https://createadish.com/images/dietary-guide-og.png';

const DietaryGuide: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  const diets = [
    {
      name: 'Mediterranean Diet',
      path: '/dietary-guide/mediterranean',
      description: 'Emphasizes plant-based foods, healthy fats (olive oil), whole grains, and lean proteins (fish). Widely considered the "gold standard" for heart health and longevity.',
      tags: ['#HeartHealth', '#Longevity', '#Balanced']
    },
    {
      name: 'Flexitarian Diet',
      path: '/dietary-guide/flexitarian',
      description: 'A "flexible vegetarian" approach that encourages mostly plant-based meals while allowing for the occasional consumption of meat and animal products.',
      tags: ['#Sustainability', '#Vegetarian', '#Flexible']
    },
    {
      name: 'Keto (Ketogenic) Diet',
      path: '/dietary-guide/keto',
      description: 'A very low-carb, high-fat diet designed to put the body into a state of ketosis, where it burns fat for fuel instead of glucose.',
      tags: ['#LowCarb', '#WeightLoss', '#HighFat']
    },
    {
      name: 'DASH Diet',
      path: '/dietary-guide/dash',
      description: '"Dietary Approaches to Stop Hypertension." Focuses on reducing sodium and increasing intake of fruits, vegetables, and low-fat dairy to manage blood pressure.',
      tags: ['#HeartHealth', '#Medical', '#LowSodium']
    },
    {
      name: 'MIND Diet',
      path: '/dietary-guide/mind',
      description: 'A hybrid of the Mediterranean and DASH diets specifically designed to boost brain health and prevent cognitive decline through foods like berries, leafy greens, and nuts.',
      tags: ['#BrainHealth', '#Cognitive', '#Longevity']
    },
    {
      name: 'Intermittent Fasting (IF)',
      path: '/dietary-guide/intermittent-fasting',
      description: 'An eating pattern that cycles between periods of fasting and eating, focusing more on when you eat rather than what you eat.',
      tags: ['#Lifestyle', '#WeightLoss', '#Wellness']
    },
    {
      name: 'Vegan Diet',
      path: '/dietary-guide/vegan',
      description: 'A strictly plant-based lifestyle that excludes all animal products, including meat, dairy, eggs, and often honey.',
      tags: ['#PlantBased', '#Ethics', '#Vegan']
    },
    {
      name: 'Paleo Diet',
      path: '/dietary-guide/paleo',
      description: 'Based on foods likely eaten during the Paleolithic era, focusing on whole foods like lean meats, fish, fruits, vegetables, nuts, and seeds while avoiding grains and dairy.',
      tags: ['#WholeFoods', '#GrainsFree', '#Ancestral']
    },
    {
      name: 'Low-FODMAP Diet',
      path: '/dietary-guide/low-fodmap',
      description: 'A temporary restrictive diet used to identify trigger foods for people with IBS by limiting certain fermentable carbohydrates.',
      tags: ['#GutHealth', '#IBS', '#Specialized']
    },
    {
      name: 'Whole30',
      path: '/dietary-guide/whole30',
      description: 'A 30-day nutritional reset that eliminates sugar, alcohol, grains, legumes, and dairy to identify food sensitivities and "reset" cravings.',
      tags: ['#Reset', '#CleanEating', '#Nutritional']
    },
    {
      name: 'Anti-Inflammatory Diet',
      path: '/dietary-guide/anti-inflammatory',
      description: 'Focuses on foods that reduce chronic inflammation, such as fatty fish, berries, turmeric, and leafy greens, to support overall immune function.',
      tags: ['#ImmuneHealth', '#Wellness', '#Healing']
    },
    {
      name: 'Gluten-Free Diet',
      path: '/dietary-guide/gluten-free',
      description: 'Strictly excludes gluten, a protein found in wheat, barley, and rye; essential for those with Celiac disease or gluten sensitivity.',
      tags: ['#AllergyFriendly', '#Celiac', '#Specialized']
    }
  ];

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="dietary guide, keto diet, vegan diet, paleo diet, mediterranean diet, nutrition, healthy eating, dietary patterns, meal planning" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={PAGE_URL} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={PAGE_URL} />
        
        {/* Structured Data - Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "The Ultimate Dietary Guide: Tailoring AI Recipes to Your Lifestyle",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": {
              "@type": "Organization",
              "name": "Create A Dish"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Create A Dish",
              "logo": {
                "@type": "ImageObject",
                "url": "https://createadish.com/logo.png"
              }
            },
            "datePublished": "2025-12-24",
            "dateModified": "2025-12-24",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": PAGE_URL
            }
          })}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        {/* Hero Section */}
        <Box
          sx={{
            height: { xs: '40vh', md: '50vh' },
            backgroundImage: `url(${dietaryGuideBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(1px)',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 2,
              }}
            >
              The Ultimate Dietary Guide
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 1,
              }}
            >
              Tailoring AI Recipes to Your Lifestyle
            </Typography>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Dietary Guide
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Explore Popular Dietary Patterns
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            An in-depth exploration of popular dietary patterns like Keto, Vegan, and Paleo. This guide teaches you how to optimize the Create A Dish generator to meet your specific nutritional goals while discovering the science and benefits behind each eating style.
          </Typography>

          {/* Diet Cards Grid */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                textAlign: 'center',
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <DietIcon />
              Choose Your Dietary Path
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 3
              }}
            >
              {diets.map((diet, index) => (
                <Card key={index} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea onClick={() => navigate(diet.path)} sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3, flexGrow: 1 }}>
                      <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {diet.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, mb: 2 }}>
                        {diet.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {diet.tags.map((tag, idx) => (
                          <Typography key={idx} variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            {tag}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Ready to Create Your Perfect Dish?
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<StarIcon />}
              onClick={handleGetStarted}
              sx={{
                minWidth: 200,
                fontSize: '1.1rem',
                py: 1.5,
                px: 4,
              }}
            >
              Get Started
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DietaryGuide;
